/**
 * Locker Management Dashboard
 *
 * Displays the 4 pickup lockers in a 2x2 grid matching physical layout,
 * with live Eufy lock status, active assignments, and recent pickup history.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';
import api from '../../services/api';

interface LockerAssignment {
  id: number;
  order_id: number | null;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  passcode: string;
  assigned_at: string | null;
  status: string;
}

interface Locker {
  id: number;
  number: number;
  name: string;
  eufy_device_id: string;
  capacity_bags: number;
  battery_level: number | null;
  last_seen: string | null;
  is_occupied: boolean;
  assignment: LockerAssignment | null;
}

interface ActiveAssignment {
  id: number;
  locker_number: number;
  locker_name: string;
  order_id: number | null;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  passcode: string;
  assigned_at: string | null;
}

interface RecentPickup {
  id: number;
  locker_number: number;
  locker_name: string;
  order_number: string;
  customer_name: string;
  picked_up_at: string | null;
}

interface DashboardStats {
  total_lockers: number;
  occupied: number;
  available: number;
  active_assignments: number;
}

interface DashboardData {
  lockers: Locker[];
  active_assignments: ActiveAssignment[];
  recent_pickups: RecentPickup[];
  stats: DashboardStats;
}

interface EufyLockStatus {
  serial: string;
  name: string;
  locked: boolean | null;
  battery: number | null;
}

interface EufyAuthStatus {
  connected: boolean;
  devicesCount: number;
  pendingCaptcha: boolean;
  pendingTfa: boolean;
  message: string;
}

const LockerDashboard: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selectedLocker, setSelectedLocker] = useState<Locker | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [eufyStatus, setEufyStatus] = useState<Map<string, EufyLockStatus>>(new Map());
  const [eufyConnected, setEufyConnected] = useState<boolean | null>(null);

  // Eufy auth state
  const [eufyAuthStatus, setEufyAuthStatus] = useState<EufyAuthStatus | null>(null);
  const [captchaImage, setCaptchaImage] = useState<string | null>(null);
  const [captchaSolution, setCaptchaSolution] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/store/lockers/dashboard/');
      setData(response.data);
      setError('');
    } catch (err: any) {
      console.error('Failed to load locker dashboard:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEufyStatus = useCallback(async () => {
    try {
      // Call Django proxy endpoint for Eufy status
      const response = await api.get('/store/lockers/eufy-status/');
      const data = response.data;
      const statusMap = new Map<string, EufyLockStatus>();

      if (data.locks && Array.isArray(data.locks)) {
        data.locks.forEach((lock: EufyLockStatus) => {
          statusMap.set(lock.serial, lock);
        });
      }

      setEufyStatus(statusMap);
      setEufyConnected(data.connected || false);
    } catch (err) {
      console.log('Eufy status unavailable:', err);
      setEufyConnected(false);
    }
  }, []);

  // Load Eufy auth status to check for captcha/2FA
  const loadEufyAuthStatus = useCallback(async () => {
    try {
      const response = await api.get('/store/lockers/eufy-auth/status/');
      setEufyAuthStatus(response.data);

      // If captcha is pending, load the image
      if (response.data.pendingCaptcha) {
        loadCaptchaImage();
      }
    } catch (err) {
      console.log('Eufy auth status unavailable:', err);
    }
  }, []);

  const loadCaptchaImage = async () => {
    try {
      const response = await api.get('/store/lockers/eufy-auth/captcha/');
      if (response.data.captcha) {
        // Handle different captcha formats
        const captcha = response.data.captcha;
        if (captcha.startsWith('data:')) {
          setCaptchaImage(captcha);
        } else {
          setCaptchaImage(`data:image/png;base64,${captcha}`);
        }
      }
    } catch (err) {
      console.log('Failed to load captcha:', err);
    }
  };

  const submitCaptcha = async () => {
    if (!captchaSolution.trim()) {
      showToast('error', 'Please enter the captcha text');
      return;
    }

    try {
      setAuthLoading(true);
      await api.post('/store/lockers/eufy-auth/captcha/submit/', { captcha: captchaSolution });
      showToast('success', 'Captcha submitted! Reconnecting...');
      setCaptchaSolution('');
      setCaptchaImage(null);

      // Wait and reload status
      setTimeout(() => {
        loadEufyAuthStatus();
        loadEufyStatus();
      }, 3000);
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to submit captcha');
    } finally {
      setAuthLoading(false);
    }
  };

  const submitTfa = async () => {
    if (!tfaCode.trim()) {
      showToast('error', 'Please enter the 2FA code');
      return;
    }

    try {
      setAuthLoading(true);
      await api.post('/store/lockers/eufy-auth/tfa/', { code: tfaCode });
      showToast('success', '2FA code submitted! Reconnecting...');
      setTfaCode('');

      // Wait and reload status
      setTimeout(() => {
        loadEufyAuthStatus();
        loadEufyStatus();
      }, 3000);
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to submit 2FA code');
    } finally {
      setAuthLoading(false);
    }
  };

  const forceReconnect = async () => {
    try {
      setAuthLoading(true);
      await api.post('/store/lockers/eufy-auth/reconnect/');
      showToast('success', 'Reconnecting to Eufy...');

      // Wait and reload status
      setTimeout(() => {
        loadEufyAuthStatus();
        loadEufyStatus();
      }, 5000);
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to reconnect');
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?next=/store/lockers');
      return;
    }
    loadDashboard();
    loadEufyStatus();
    loadEufyAuthStatus();

    // Refresh dashboard every 30 seconds
    const dashboardInterval = setInterval(loadDashboard, 30000);
    // Refresh Eufy status every 30 seconds
    const eufyInterval = setInterval(loadEufyStatus, 30000);
    // Refresh auth status every 30 seconds
    const authInterval = setInterval(loadEufyAuthStatus, 30000);

    return () => {
      clearInterval(dashboardInterval);
      clearInterval(eufyInterval);
      clearInterval(authInterval);
    };
  }, [isAuthenticated, navigate, loadDashboard, loadEufyStatus, loadEufyAuthStatus]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleMarkPickedUp = async (assignmentId: number) => {
    try {
      setActionLoading(assignmentId);
      await api.post(`/store/lockers/assignments/${assignmentId}/pickup/`);
      showToast('success', 'Marked as picked up!');
      setSelectedLocker(null);
      loadDashboard();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to mark as picked up');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelAssignment = async (assignmentId: number) => {
    if (!window.confirm('Are you sure you want to cancel this locker assignment?')) return;

    try {
      setActionLoading(assignmentId);
      await api.post(`/store/lockers/assignments/${assignmentId}/cancel/`);
      showToast('success', 'Assignment cancelled');
      setSelectedLocker(null);
      loadDashboard();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to cancel assignment');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTimeAgo = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getLockerStatusColor = (locker: Locker): string => {
    if (locker.battery_level !== null && locker.battery_level < 20) return '#dc3545'; // Red - low battery
    if (locker.is_occupied) return '#ffc107'; // Yellow - occupied
    return '#28a745'; // Green - available
  };

  const getLockerStatusText = (locker: Locker): string => {
    if (locker.battery_level !== null && locker.battery_level < 20) return 'LOW BATTERY';
    if (locker.is_occupied) return 'OCCUPIED';
    return 'AVAILABLE';
  };

  // Get Eufy lock status for a locker
  const getEufyLockStatus = (locker: Locker): EufyLockStatus | null => {
    // Primary: Match by eufy_device_id stored in locker record
    if (locker.eufy_device_id && eufyStatus.has(locker.eufy_device_id)) {
      return eufyStatus.get(locker.eufy_device_id) || null;
    }

    // Fallback: Match by name containing locker number
    const entries = Array.from(eufyStatus.entries());
    for (let i = 0; i < entries.length; i++) {
      const [, status] = entries[i];
      if (status.name?.toLowerCase().includes(`locker ${locker.number}`) ||
          status.name?.toLowerCase().includes(`locker${locker.number}`)) {
        return status;
      }
    }
    return null;
  };

  const renderLockStatus = (locker: Locker) => {
    if (eufyConnected === false) {
      return <div style={styles.lockStatusUnavailable}>Status unavailable</div>;
    }

    const lockStatus = getEufyLockStatus(locker);

    if (!lockStatus) {
      return <div style={styles.lockStatusUnavailable}>No lock linked</div>;
    }

    const isLocked = lockStatus.locked;

    return (
      <div style={styles.lockStatusContainer}>
        {isLocked === true && (
          <span style={styles.lockStatusLocked}>🔒 Locked</span>
        )}
        {isLocked === false && (
          <span style={styles.lockStatusUnlocked}>🔓 Unlocked</span>
        )}
        {isLocked === null && (
          <span style={styles.lockStatusUnknown}>⚠️ Unknown</span>
        )}
        {lockStatus.battery !== null && (
          <span style={styles.batteryInline}>
            🔋 {lockStatus.battery}%
          </span>
        )}
      </div>
    );
  };

  // Get locker position description
  const getLockerPosition = (lockerNumber: number): string => {
    switch (lockerNumber) {
      case 1: return 'Top Left';
      case 2: return 'Top Right';
      case 3: return 'Bottom Left';
      case 4: return 'Bottom Right';
      default: return '';
    }
  };

  // Sort lockers for 2x2 grid display: [1, 2, 3, 4] becomes grid positions
  const getSortedLockers = (): Locker[] => {
    if (!data?.lockers) return [];
    return [...data.lockers].sort((a, b) => a.number - b.number);
  };

  if (loading && !data) {
    return (
      <div style={styles.page}>
        <StoreHeader />
        <div style={styles.container}>
          <div style={styles.loading}>Loading locker dashboard...</div>
        </div>
      </div>
    );
  }

  const sortedLockers = getSortedLockers();

  return (
    <div style={styles.page}>
      <StoreHeader />

      <div style={styles.container}>
        {/* Toast notification */}
        {toast && (
          <div style={{
            ...styles.toast,
            backgroundColor: toast.type === 'success' ? '#d4edda' : '#f8d7da',
            color: toast.type === 'success' ? '#155724' : '#721c24',
          }}>
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>🔐 Locker Management</h1>
            {eufyConnected === true && (
              <span style={styles.eufyConnected}>● Eufy Connected</span>
            )}
            {eufyConnected === false && (
              <span style={styles.eufyDisconnected}>● Eufy Offline</span>
            )}
          </div>
          <div style={styles.stats}>
            <span style={styles.statItem}>
              <span style={styles.statValue}>{data?.stats.available || 0}</span>
              <span style={styles.statLabel}>Available</span>
            </span>
            <span style={styles.statItem}>
              <span style={styles.statValue}>{data?.stats.occupied || 0}</span>
              <span style={styles.statLabel}>Occupied</span>
            </span>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Eufy Auth Challenge Banner */}
        {(eufyAuthStatus?.pendingCaptcha || eufyAuthStatus?.pendingTfa) && (
          <div style={styles.authBanner}>
            <div style={styles.authBannerHeader}>
              <span style={styles.authBannerIcon}>⚠️</span>
              <strong>Eufy Authentication Required</strong>
            </div>

            {eufyAuthStatus.pendingCaptcha && (
              <div style={styles.authForm}>
                <p>Eufy requires a captcha to be solved:</p>
                {captchaImage && (
                  <img
                    src={captchaImage}
                    alt="Captcha"
                    style={styles.captchaImage}
                  />
                )}
                <div style={styles.authInputRow}>
                  <input
                    type="text"
                    value={captchaSolution}
                    onChange={(e) => setCaptchaSolution(e.target.value)}
                    placeholder="Enter captcha text"
                    style={styles.authInput}
                    disabled={authLoading}
                  />
                  <button
                    onClick={submitCaptcha}
                    style={styles.authButton}
                    disabled={authLoading}
                  >
                    {authLoading ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            )}

            {eufyAuthStatus.pendingTfa && (
              <div style={styles.authForm}>
                <p>Eufy requires a 2FA code. Check your email for the code:</p>
                <div style={styles.authInputRow}>
                  <input
                    type="text"
                    value={tfaCode}
                    onChange={(e) => setTfaCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    style={styles.authInput}
                    disabled={authLoading}
                  />
                  <button
                    onClick={submitTfa}
                    style={styles.authButton}
                    disabled={authLoading}
                  >
                    {authLoading ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reconnect button when disconnected */}
        {eufyConnected === false && !eufyAuthStatus?.pendingCaptcha && !eufyAuthStatus?.pendingTfa && (
          <div style={styles.reconnectBanner}>
            <span>Eufy service is not connected to the lock system.</span>
            <button
              onClick={forceReconnect}
              style={styles.reconnectButton}
              disabled={authLoading}
            >
              {authLoading ? 'Reconnecting...' : 'Reconnect'}
            </button>
          </div>
        )}

        {/* 2x2 Locker Grid - Matches Physical Layout */}
        <div style={styles.lockerGridContainer}>
          <div style={styles.gridLabel}>Physical Locker Layout</div>
          <div style={styles.lockerGrid}>
            {sortedLockers.map(locker => (
              <div
                key={locker.id}
                style={{
                  ...styles.lockerCard,
                  borderColor: getLockerStatusColor(locker),
                  cursor: locker.is_occupied ? 'pointer' : 'default',
                }}
                onClick={() => locker.is_occupied && setSelectedLocker(locker)}
              >
                <div style={styles.lockerHeader}>
                  <span style={styles.lockerNumber}>LOCKER {locker.number}</span>
                  <span style={styles.lockerPosition}>{getLockerPosition(locker.number)}</span>
                </div>

                <div style={{
                  ...styles.lockerStatus,
                  backgroundColor: getLockerStatusColor(locker),
                }}>
                  {getLockerStatusText(locker)}
                </div>

                {/* Live Lock Status */}
                {renderLockStatus(locker)}

                {locker.is_occupied && locker.assignment && (
                  <div style={styles.lockerAssignee}>
                    <div style={styles.assigneeName}>{locker.assignment.customer_name}</div>
                    <div style={styles.assigneeOrder}>Order #{locker.assignment.order_number}</div>
                  </div>
                )}

                {!locker.is_occupied && (
                  <div style={styles.capacityInfo}>
                    Capacity: {locker.capacity_bags} bags
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Active Assignments */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Active Pickups Waiting</h2>
          {data?.active_assignments.length === 0 ? (
            <div style={styles.emptyState}>No active assignments</div>
          ) : (
            <div style={styles.assignmentList}>
              {data?.active_assignments.map(assignment => (
                <div key={assignment.id} style={styles.assignmentRow}>
                  <div style={styles.assignmentLocker}>
                    Locker {assignment.locker_number}
                  </div>
                  <div style={styles.assignmentCustomer}>
                    {assignment.customer_name}
                  </div>
                  <div style={styles.assignmentCode}>
                    Code: <strong>{assignment.passcode}</strong>
                  </div>
                  <div style={styles.assignmentTime}>
                    {formatTimeAgo(assignment.assigned_at)}
                  </div>
                  <button
                    style={styles.pickupBtn}
                    onClick={() => handleMarkPickedUp(assignment.id)}
                    disabled={actionLoading === assignment.id}
                  >
                    {actionLoading === assignment.id ? '...' : '✓ Picked Up'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Pickups */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Recent Pickups (Last 7 Days)</h2>
          {data?.recent_pickups.length === 0 ? (
            <div style={styles.emptyState}>No recent pickups</div>
          ) : (
            <div style={styles.historyList}>
              {data?.recent_pickups.map(pickup => (
                <div key={pickup.id} style={styles.historyRow}>
                  <div style={styles.historyLocker}>
                    Locker {pickup.locker_number}
                  </div>
                  <div style={styles.historyCustomer}>
                    {pickup.customer_name}
                  </div>
                  <div style={styles.historyTime}>
                    Picked up {formatDateTime(pickup.picked_up_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Locker Detail Modal */}
      {selectedLocker && selectedLocker.assignment && (
        <div style={styles.modalOverlay} onClick={() => setSelectedLocker(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Locker {selectedLocker.number} - {getLockerPosition(selectedLocker.number)}
              </h2>
              <button style={styles.closeBtn} onClick={() => setSelectedLocker(null)}>×</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Customer:</span>
                <span style={styles.detailValue}>{selectedLocker.assignment.customer_name}</span>
              </div>
              {selectedLocker.assignment.customer_phone && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Phone:</span>
                  <span style={styles.detailValue}>{selectedLocker.assignment.customer_phone}</span>
                </div>
              )}
              {selectedLocker.assignment.customer_email && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Email:</span>
                  <span style={styles.detailValue}>{selectedLocker.assignment.customer_email}</span>
                </div>
              )}
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Order:</span>
                <span style={styles.detailValue}>{selectedLocker.assignment.order_number}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Assigned:</span>
                <span style={styles.detailValue}>
                  {formatDateTime(selectedLocker.assignment.assigned_at)}
                </span>
              </div>

              {/* Lock Status in Modal */}
              <div style={styles.modalLockStatus}>
                {renderLockStatus(selectedLocker)}
              </div>

              <div style={styles.passcodeSection}>
                <span style={styles.passcodeLabel}>ACCESS CODE</span>
                <span style={styles.passcodeValue}>{selectedLocker.assignment.passcode}</span>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.cancelBtn}
                onClick={() => handleCancelAssignment(selectedLocker.assignment!.id)}
                disabled={actionLoading === selectedLocker.assignment.id}
              >
                Cancel Assignment
              </button>
              <button
                style={styles.confirmPickupBtn}
                onClick={() => handleMarkPickedUp(selectedLocker.assignment!.id)}
                disabled={actionLoading === selectedLocker.assignment.id}
              >
                {actionLoading === selectedLocker.assignment.id ? 'Processing...' : '✓ Mark Picked Up'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
    fontSize: '1.1rem',
  },
  toast: {
    position: 'fixed',
    top: '80px',
    right: '20px',
    padding: '1rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '600',
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  eufyConnected: {
    fontSize: '0.8rem',
    color: '#28a745',
    marginTop: '0.25rem',
    display: 'block',
  },
  eufyDisconnected: {
    fontSize: '0.8rem',
    color: '#dc3545',
    marginTop: '0.25rem',
    display: 'block',
  },
  stats: {
    display: 'flex',
    gap: '1.5rem',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#666',
    textTransform: 'uppercase',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  // Auth banner styles
  authBanner: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  authBannerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    fontSize: '1.1rem',
    color: '#856404',
  },
  authBannerIcon: {
    fontSize: '1.5rem',
  },
  authForm: {
    marginTop: '1rem',
  },
  captchaImage: {
    maxWidth: '100%',
    maxHeight: '150px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  authInputRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
  },
  authInput: {
    flex: 1,
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
  },
  authButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    backgroundColor: '#2D5A27',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  reconnectBanner: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '12px',
    padding: '1rem 1.5rem',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  reconnectButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  // 2x2 Grid Container
  lockerGridContainer: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  gridLabel: {
    fontSize: '0.85rem',
    color: '#666',
    textAlign: 'center',
    marginBottom: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  lockerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  lockerCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.25rem',
    textAlign: 'center',
    border: '3px solid',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    minHeight: '180px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  lockerHeader: {
    marginBottom: '0.75rem',
  },
  lockerNumber: {
    display: 'block',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#333',
  },
  lockerPosition: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#888',
    marginTop: '0.125rem',
  },
  lockerStatus: {
    display: 'inline-block',
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    color: '#fff',
    fontWeight: '600',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  },
  lockStatusContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '0.5rem',
    flexWrap: 'wrap',
  },
  lockStatusLocked: {
    color: '#28a745',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  lockStatusUnlocked: {
    color: '#fd7e14',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  lockStatusUnknown: {
    color: '#dc3545',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  lockStatusUnavailable: {
    color: '#999',
    fontSize: '0.8rem',
    fontStyle: 'italic',
    marginTop: '0.5rem',
  },
  batteryInline: {
    fontSize: '0.8rem',
    color: '#666',
  },
  lockerAssignee: {
    marginTop: '0.75rem',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  assigneeName: {
    fontSize: '0.95rem',
    color: '#333',
    fontWeight: '600',
  },
  assigneeOrder: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.25rem',
  },
  capacityInfo: {
    marginTop: 'auto',
    fontSize: '0.8rem',
    color: '#888',
    paddingTop: '0.5rem',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 1rem 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: '#999',
    fontStyle: 'italic',
  },
  assignmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  assignmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#fff8e1',
    borderRadius: '8px',
    border: '1px solid #ffe082',
    flexWrap: 'wrap',
  },
  assignmentLocker: {
    fontWeight: '600',
    color: '#333',
    minWidth: '100px',
  },
  assignmentCustomer: {
    flex: 1,
    minWidth: '150px',
    color: '#333',
  },
  assignmentCode: {
    fontFamily: 'monospace',
    fontSize: '1rem',
    color: '#333',
    minWidth: '120px',
  },
  assignmentTime: {
    color: '#666',
    fontSize: '0.9rem',
    minWidth: '80px',
  },
  pickupBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  historyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    flexWrap: 'wrap',
  },
  historyLocker: {
    fontWeight: '500',
    color: '#333',
    minWidth: '100px',
  },
  historyCustomer: {
    flex: 1,
    minWidth: '150px',
    color: '#333',
  },
  historyTime: {
    color: '#666',
    fontSize: '0.9rem',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #eee',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#999',
    cursor: 'pointer',
    padding: '0.25rem',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    fontSize: '0.95rem',
  },
  detailLabel: {
    color: '#666',
  },
  detailValue: {
    fontWeight: '500',
    color: '#333',
    textAlign: 'right',
  },
  modalLockStatus: {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  passcodeSection: {
    marginTop: '1rem',
    padding: '1.5rem',
    backgroundColor: '#f0f8ff',
    borderRadius: '12px',
    textAlign: 'center',
  },
  passcodeLabel: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '0.5rem',
  },
  passcodeValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    color: '#1a73e8',
    letterSpacing: '4px',
  },
  modalFooter: {
    display: 'flex',
    gap: '1rem',
    padding: '1.25rem 1.5rem',
    borderTop: '1px solid #eee',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem 1rem',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  confirmPickupBtn: {
    flex: 1,
    padding: '0.75rem 1rem',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.95rem',
  },
};

export default LockerDashboard;
